import { ActionIcon, Box, FileInput, Stack } from "@mantine/core";
import React, { useEffect, useState } from "react";
import ImageView from "./ImageView";
import { useTranslation } from "react-i18next";
import { IconX } from "@tabler/icons-react";
import { Organization } from "types/accountManagementTypes";

interface OrgLogoViewProps {
  imageSize?: { width: number; height: number };
  organization?: Organization;
  isEditMode: boolean;
  isPreview?: boolean;
  onLogoSelected?: (file: File | null) => void;
}

const OrgLogoView: React.FC<OrgLogoViewProps> = ({
  imageSize,
  organization,
  isEditMode,
  isPreview,
  onLogoSelected,
}) => {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [showDeleteButton, setShowDeleteButton] = useState(false);
  const [showImageView, setShowImageView] = useState(true);

  const [activeImage, setActiveImage] = useState<"original" | "selected" | "removed" | "none">(
    "original",
  );

  useEffect(() => {
    if (selectedFile || (isPreview && organization?.logoFile)) {
      setActiveImage("selected");
    } else {
      setActiveImage(!!organization?.logoUrl ? "original" : "none");
    }
  }, [organization?.logoUrl, selectedFile, isPreview]);

  useEffect(() => {
    if (!isEditMode) {
      setSelectedFile(null);
    }
  }, [isEditMode, t]);

  useEffect(() => {
    setShowDeleteButton(!isPreview && isEditMode && activeImage !== "removed");
    setShowImageView(activeImage !== "none" && activeImage !== "removed");
  }, [isPreview, isEditMode, activeImage]);

  const handleFileChange = (file: File | null) => {
    setSelectedFile(file);
    onLogoSelected?.(file);
    setActiveImage(file ? "selected" : "removed");
  };

  const CancelButtonView = () => (
    <Box style={{ justifyContent: "end", display: "flex" }}>
      <ActionIcon
        variant="subtle"
        color="gray"
        radius="xl"
        size={28}
        style={{ backgroundColor: "rgba(0, 0, 0, 0.1)" }}
        aria-label={t("OrganizationSettings.removeSelectedFile") || "Remove selected file"}
        onClick={() => handleFileChange(null)}
      >
        <IconX />
      </ActionIcon>
    </Box>
  );

  return (
    <Stack
      key={organization?.logoUrl}
      style={{ width: "100%", height: "100%", justifyContent: "center", alignItems: "end" }}
      className="org-logo-view"
    >
      {showDeleteButton && <CancelButtonView />}
      {showImageView && (
        <ImageView
          imageSize={imageSize}
          url={organization?.logoUrl}
          file={isEditMode ? selectedFile : isPreview ? organization?.logoFile : null}
          alt={organization?.name}
          style={{ width: "100%", height: "100%" }}
        />
      )}
      {isEditMode && !isPreview && (
        <FileInput
          accept="image/png,image/jpeg"
          placeholder="Upload files"
          value={selectedFile}
          onChange={handleFileChange}
        />
      )}
    </Stack>
  );
};

export default OrgLogoView;
